import { classes } from "polytype";
import { Bytes, DataModel, ReferenceModel } from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { config } from "../../config/config.js";
import { provideReact } from "../../react.js";
import type { StorageMetricsData, StorageMetricsKind } from "./types.js";

export class StorageMetrics extends ReferenceModel {
  public readonly kind: StorageMetricsKind;
  public static readonly storageUsageWarning = 75;
  public static readonly storageUsageError = 90;

  public constructor(id: string, kind: StorageMetricsKind) {
    super(id);
    this.kind = kind;
  }

  public static ofId(id: string, kind: StorageMetricsKind) {
    return new StorageMetrics(id, kind);
  }

  public static find = provideReact(
    async (id: string, type: StorageMetricsKind) => {
      const data =
        type === "project"
          ? await config.behaviors.storageMetrics.findByProject(id)
          : await config.behaviors.storageMetrics.findByServer(id);

      if (data !== undefined) {
        return new StorageMetricsDetailed(data);
      }
    },
  );

  public static get = provideReact(
    async (id: string, type: StorageMetricsKind) => {
      const storageMetrics = await this.find(id, type);
      assertObjectFound(storageMetrics, "storageMetrics", id);
      return storageMetrics;
    },
  );
}

export class StorageMetricsDetailed extends classes(
  DataModel<StorageMetricsData>,
  StorageMetrics,
) {
  public readonly childStatistics?: StorageMetricsDetailed[];
  public readonly notificationActive: boolean;
  public readonly notificationThreshold?: Bytes;
  public readonly totalStorageUsage: Bytes;
  public readonly notificationThresholdSuggestion: Bytes;
  public readonly storageLimit?: Bytes;
  public readonly notificationThresholdUsedAsLimit: boolean;
  public readonly storageAlmostExceeded: boolean;
  public readonly storageExceeded: boolean;
  public readonly databaseSize: Bytes;
  public readonly webspaceSize: Bytes;
  public readonly backupSize: Bytes;
  public readonly mailSize: Bytes;
  public readonly containerSize: Bytes;

  public constructor(data: StorageMetricsData) {
    super([data], [data.id, data.kind]);
    this.childStatistics = data.childStatistics?.map(
      (s) => new StorageMetricsDetailed(s),
    );
    this.notificationActive = !!data.notificationThresholdInBytes;
    this.notificationThreshold = data.notificationThresholdInBytes
      ? Bytes.of(data.notificationThresholdInBytes, "bytes")
      : undefined;
    this.totalStorageUsage = Bytes.of(data.meta.totalUsageInBytes, "bytes");
    this.storageLimit = data.meta.limitInBytes
      ? Bytes.of(data.meta.limitInBytes, "bytes")
      : undefined;

    this.notificationThresholdSuggestion = this.storageLimit
      ? this.storageLimit.multiply(0.8)
      : this.totalStorageUsage.in("GiB") < 1
        ? Bytes.of(1, "GiB")
        : this.totalStorageUsage.multiply(1.5);
    if (
      this.storageLimit &&
      this.storageLimit.isGreaterThan(this.notificationThresholdSuggestion)
    ) {
      this.notificationThresholdSuggestion = this.storageLimit;
    }
    this.notificationThresholdUsedAsLimit =
      !!data.meta.notificationThresholdUsedAsLimit;
    this.storageExceeded = data.meta.totalUsageInPercentage
      ? data.meta.totalUsageInPercentage >= StorageMetrics.storageUsageError
      : false;
    this.storageAlmostExceeded = data.meta.totalUsageInPercentage
      ? !this.storageExceeded &&
        data.meta.totalUsageInPercentage >= StorageMetrics.storageUsageWarning
      : false;

    const mySqlSize = Bytes.of(
      data.statisticCategories?.find((c) => c.kind === "mysqlDatabase")
        ?.totalUsageInBytes ?? 0,
      "bytes",
    );
    const redisSize = Bytes.of(
      data.statisticCategories?.find((c) => c.kind === "redisDatabase")
        ?.totalUsageInBytes ?? 0,
      "bytes",
    );
    this.databaseSize = mySqlSize.add(redisSize);

    this.webspaceSize = Bytes.of(
      data.statisticCategories?.find((c) => c.kind === "webspace")
        ?.totalUsageInBytes ?? 0,
      "bytes",
    );

    this.backupSize = Bytes.of(
      data.statisticCategories?.find((c) => c.kind === "projectBackup")
        ?.totalUsageInBytes ?? 0,
      "bytes",
    );

    this.mailSize = Bytes.of(
      data.statisticCategories?.find((c) => c.kind === "mailAddress")
        ?.totalUsageInBytes ?? 0,
      "bytes",
    );

    this.containerSize = Bytes.of(
      data.statisticCategories?.find((c) => c.kind === "containerVolume")
        ?.totalUsageInBytes ?? 0,
      "bytes",
    );
  }
}
